import React from 'react';
import { motion } from 'framer-motion';
import { getLevelIndex, UI_TEXT } from '../config/gameConfig';

const HUD = ({ level, levelName, collectedDrafts, totalDrafts, time, abilityCooldown, isAbilityActive, isMobile }) => {
  const cooldownProgress = Math.min(100, (abilityCooldown / (isMobile ? 5000 : 7000)) * 100);
  const isReady = abilityCooldown === 0;

  // Get sequential level number (1-based)
  const levelNumber = getLevelIndex(level) + 1;

  return (
    <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none h-full">
      {/* Top Bar */}
      <div className={`bg-black/60 backdrop-blur-sm border-b border-gray-800 ${isMobile ? 'p-2' : 'p-4'} absolute top-0 left-0 right-0`}>
        <div className={`flex justify-between items-center ${isMobile ? 'max-w-full' : 'max-w-7xl'} mx-auto`}>
          <div className="flex items-center space-x-3">
            <div className="flex flex-col">
              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 uppercase tracking-widest`}>
                {UI_TEXT.hud.levelPrefix} {levelNumber}
              </span>
              <span className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-white font-mono`}>
                {levelName}
              </span>
            </div>
          </div>

          <div className={`flex items-center ${isMobile ? 'space-x-4' : 'space-x-8'}`}>
            {/* Drafts Counter */}
            <div className="flex flex-col items-end">
              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 uppercase tracking-widest`}>
                {UI_TEXT.hud.draftsLabel}
              </span>
              <div className="flex items-center space-x-1">
                <span className={`text-yellow-400 ${isMobile ? 'text-sm' : 'text-base'}`}>♪</span>
                <span className={`font-mono ${isMobile ? 'text-base' : 'text-xl'}`}>
                  {collectedDrafts}/{totalDrafts}
                </span>
              </div>
            </div>

            {/* Timer */}
            <div className="flex flex-col items-end w-20">
              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 uppercase tracking-widest`}>
                {UI_TEXT.hud.timeLabel}
              </span>
              <span className={`font-mono ${isMobile ? 'text-base' : 'text-xl'} text-blue-400`}>
                {Math.floor(time / 60)}:{(time % 60).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Ability Indicator - Desktop Only (Moved to bottom center) */}
      {!isMobile && (
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex flex-col items-center">
          <div
            className="relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300"
            style={{
              opacity: isReady ? 1 : 0.5,
              backgroundColor: isReady ? '#06b6d4' : '#1f2937',
              boxShadow: isReady ? '0 0 20px rgba(6, 182, 212, 0.6)' : 'none',
              transform: isReady ? 'scale(1.05)' : 'scale(1)'
            }}
          >
            {isAbilityActive ? (
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 0.5 }}
                className="text-white font-bold text-3xl"
              >
                ✋
              </motion.div>
            ) : (
              <div className="text-center">
                 <span className={`text-sm font-bold uppercase ${isReady ? 'text-black' : 'text-cyan-400'}`}>
                  SPACE
                </span>
              </div>
            )}

            {/* Cooldown Ring */}
            <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none">
              <circle
                cx="40"
                cy="40"
                r="38"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                className="text-black/30"
              />
              <circle
                cx="40"
                cy="40"
                r="38"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                strokeDasharray="238"
                strokeDashoffset={238 * (cooldownProgress / 100)}
                className="text-white transition-all duration-100"
                style={{ opacity: isReady ? 0 : 1 }}
              />
            </svg>
          </div>

          <div className="mt-3 text-xs uppercase tracking-widest font-bold text-center" style={{ color: isReady ? '#06b6d4' : '#6b7280' }}>
            {isAbilityActive
              ? UI_TEXT.hud.abilityActive
              : isReady
              ? UI_TEXT.hud.abilityReady
              : UI_TEXT.hud.abilityRecharging}
          </div>
        </div>
      )}
    </div>
  );
};

export default HUD;